import { Claude, Gemini, Meta, OpenAI, Perplexity } from "@lobehub/icons";
import { type ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@promptwatch/ui/components/chart";
import DottedMap from "dotted-map";
import { BotIcon, GlobeIcon, TrendingUpIcon } from "lucide-react";
import Image from "next/image";
import { Area, AreaChart, CartesianGrid } from "recharts";

const FeaturesSection = () => {
  return (
    <section className="overflow-hidden" id="features">
      <div className="mx-auto grid  md:grid-cols-2">
        <div className="bg-white">
          <div className="p-6 sm:p-12">
            <span className="text-muted-foreground flex items-center gap-2">
              <GlobeIcon className="size-4" />
              Localization
            </span>

            <p className="mt-8 text-2xl font-semibold">
              Monitor AI in any language with <span className="text-muted-foreground">brand analytics</span>
            </p>
          </div>

          <div aria-hidden className="relative">
            <div className="absolute inset-0 z-10 m-auto size-fit">
              <div className="rounded-(--radius) bg-background z-1 relative flex size-fit w-fit items-center gap-2 border px-3 py-1 text-md font-medium shadow-md shadow-zinc-950/5">
                <span className="text-lg">🇳🇱</span> Conversation in Dutch
              </div>
              <div className="rounded-(--radius) bg-background absolute inset-2 -bottom-2 mx-auto border px-3 py-4 text-xs font-medium shadow-md shadow-zinc-950/5" />
            </div>

            <div className="relative overflow-hidden">
              <div className="bg-radial z-1 to-background absolute inset-0 from-transparent to-75%" />
              <Map />
            </div>
          </div>
        </div>
        <div className="overflow-hidden border-t bg-white p-6 sm:p-12 md:border-0 md:border-l">
          <div className="relative z-10">
            <span className="text-muted-foreground flex items-center gap-2">
              <BotIcon className="size-4" />
              Track AI conversations
            </span>

            <p className="my-8 text-2xl font-semibold">
              Track AI conversations with any AI model <span className="text-muted-foreground">in real-time</span>
            </p>
          </div>
          <div aria-hidden className="flex flex-col gap-8">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-xs">Sat 22 Feb</span>
              </div>
              <div className="rounded-(--radius) bg-background mt-1.5 w-3/5 border p-3 text-sm">
                What is the best CRM for small businesses?
              </div>
            </div>

            <div>
              <div className="flex items-center justify-end gap-1 text-xs pb-2">
                <OpenAI className="size-4" /> ChatGPT 4o
              </div>
              <div className="rounded-(--radius) mb-1 ml-auto w-3/5 bg-blue-600 p-3 text-sm text-white">
                The best CRM for small businesses is HubSpot or if your business is more complex, you can use Salesforce.
              </div>
            </div>
          </div>
        </div>
        <div className="col-span-full border-y px-6 sm:px-0 py-6 sm:py-12">
          <div className="mx-auto max-w-5xl space-y-8 px-0  md:px-12 md:space-y-16">
            <div className="grid items-start gap-6 md:grid-cols-2">
              <div className="space-y-3 text-left">
                <h2 className="text-3xl font-semibold lg:text-3xl">
                  AI search market size is <span className="text-muted-foreground"> growing rapidly</span> in 2025
                </h2>
                <p className="text-muted-foreground">
                  Users are <span className="font-medium text-foreground">rapidly shifting</span> from traditional search engines to AI
                  assistants, creating new visibility channels across major AI models in this{" "}
                  <span className="font-medium text-foreground">growing market</span>.
                </p>
              </div>
              <div className="pt-3">
                <blockquote className="border-l-4 pl-4">
                  <p className="text-sm font-medium text-muted-foreground">
                    Can AI-Powered Search Engine Overtake Google? It Has for Me. A start-up called Perplexity reveals what’s possible when a
                    search engine is built from the ground up with artificial intelligence.
                  </p>

                  <div className="mt-2 space-y-3">
                    <cite className="block font-medium text-gray-500">- Kevin Roose</cite>
                    <Image className="h-6 w-fit" src="/nyt.png" alt="New York Times Logo" height={90} width={380} />
                  </div>
                </blockquote>
              </div>
            </div>

            <div className="hidden md:grid gap-8 divide-y divide-dashed *:text-center md:grid-cols-3 md:gap-2 md:divide-x md:divide-dashed md:divide-y-0">
              <div className="space-y-4 pb-8 md:pb-0">
                <p className="text-muted-foreground text-xs hidden md:block">Weekly active users</p>
                <div className="text-5xl font-bold">400M</div>
                <div className="flex items-center justify-center gap-2">
                  <OpenAI.Combine className="h-4 w-fit" size={20} />
                </div>
              </div>
              <div className="space-y-4 pt-8 pb-8 md:pt-0 md:pb-0">
                <p className="text-muted-foreground text-xs hidden md:block">Monthly active users</p>
                <div className="text-5xl font-bold">20.8M</div>
                <div className="flex items-center justify-center gap-2">
                  <Claude.Combine type="color" className="h-4 w-fit" size={20} />
                </div>
              </div>
              <div className="space-y-4 pt-8 md:pt-0">
                <p className="text-muted-foreground text-xs hidden md:block">Monthly active users</p>
                <div className="text-5xl font-bold">10.1M</div>
                <div className="flex items-center justify-center gap-2">
                  <Perplexity.Combine type="color" className="h-4 w-fit" size={20} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="relative col-span-full bg-white">
          <div className="absolute z-10 max-w-lg px-6 pr-12 pt-6 md:px-12 md:pt-12">
            <span className="text-muted-foreground flex items-center gap-2">
              <TrendingUpIcon className="size-4" />
              Model Analytics
            </span>

            <p className="my-8 text-2xl font-semibold">
              Monitor your AI visibility in real-time with <span className="text-muted-foreground">model analytics</span>
            </p>
          </div>
          <MonitoringChart />
        </div>
      </div>
    </section>
  );
};

const map = new DottedMap({ height: 55, grid: "diagonal" });

const points = map.getPoints();

const svgOptions = {
  backgroundColor: "var(--color-background)",
  color: "currentColor",
  radius: 0.15,
};

const Map = () => {
  const viewBox = "0 0 120 60";
  return (
    <svg viewBox={viewBox} style={{ background: svgOptions.backgroundColor }}>
      {points.map((point, index) => (
        <circle key={index} cx={point.x} cy={point.y} r={svgOptions.radius} fill={svgOptions.color} />
      ))}
    </svg>
  );
};

const _chartConfig = {
  desktop: {
    label: "Desktop",
    color: "#2563eb",
  },
  mobile: {
    label: "Mobile",
    color: "#60a5fa",
  },
} satisfies ChartConfig;

const chartData = [
  { month: "Jan", chatgpt: 890, claude: 1256, gemini: 80, perplexity: 1230 },
  { month: "Feb", chatgpt: 910, claude: 1456, gemini: 200, perplexity: 1430 },
  { month: "Mar", chatgpt: 1260, claude: 2126, gemini: 1802, perplexity: 1289 },
  { month: "Apr", chatgpt: 2785, claude: 3210, gemini: 3620, perplexity: 3452 },
  { month: "May", chatgpt: 3542, claude: 2876, gemini: 2590, perplexity: 3830 },
  { month: "Jun", chatgpt: 4213, claude: 5467, gemini: 3982, perplexity: 4621 },
  { month: "Jul", chatgpt: 3980, claude: 4890, gemini: 4120, perplexity: 4320 },
  { month: "Aug", chatgpt: 4560, claude: 5230, gemini: 4780, perplexity: 5100 },
  { month: "Sep", chatgpt: 5120, claude: 5780, gemini: 5340, perplexity: 5670 },
  { month: "Oct", chatgpt: 7890, claude: 8240, gemini: 7980, perplexity: 8120 },
  { month: "Nov", chatgpt: 9450, claude: 9890, gemini: 9320, perplexity: 9780 },
  {
    month: "Dec",
    chatgpt: 12210,
    claude: 12560,
    gemini: 11120,
    perplexity: 12340,
  },
];
const llmChartConfig = {
  claude: {
    label: "Claude",
    color: "#D97757",
    icon: Claude.Color,
  },

  meta: {
    label: "Meta",
    color: "#1D65C1",
    icon: Meta.Color,
  },
  gemini: {
    label: "Gemini",
    color: "#1C69FF",
    icon: Gemini.Color,
  },
  chatgpt: {
    label: "ChatGPT",
    color: "#000",
    icon: OpenAI,
  },
  perplexity: {
    label: "Perplexity",
    color: "#22B8CD",
    icon: Perplexity.Color,
  },
} satisfies ChartConfig;

export const MonitoringChart = ({
  data = chartData,
  config = llmChartConfig,
  height = 400,
  showGrid = false,
  showTooltip = true,
}: {
  data?: Array<Record<string, number | string>>;
  config?: ChartConfig;
  height?: number;
  showGrid?: boolean;
  showTooltip?: boolean;
}) => {
  return (
    <ChartContainer className="aspect-auto" config={config} style={{ height }}>
      <AreaChart
        accessibilityLayer
        data={data}
        height={height}
        margin={{
          left: 0,
          right: 0,
        }}
      >
        <defs>
          {Object.entries(llmChartConfig).map(([key, config]) => (
            <linearGradient key={key} id={`fill${key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={config.color} stopOpacity={0.8} />
              <stop offset="55%" stopColor={config.color} stopOpacity={0.1} />
            </linearGradient>
          ))}
        </defs>
        {showGrid ? (
          <CartesianGrid vertical={true} horizontal={true} strokeDasharray="3 3" />
        ) : (
          <CartesianGrid vertical={false} horizontal={true} />
        )}

        {showTooltip && <ChartTooltip active cursor={true} content={<ChartTooltipContent hideLabel className="w-64" />} />}
        {Object.entries(llmChartConfig).map(([key, config]) => (
          <Area
            key={key}
            strokeWidth={2}
            dataKey={key}
            // type="linear"
            fill={`url(#fill${key})`}
            fillOpacity={0.2}
            stroke={config.color}
            stackId="a"
          />
        ))}
      </AreaChart>
    </ChartContainer>
  );
};

export default FeaturesSection;
